import * as React from 'react';
import Dropzone from 'react-dropzone';
declare const I18n;

import { Category, Course, MediaFile } from '../../../../types';

import Header from '../../../Header';
import TextInput from '../../../inputs/TextInput';
import SelectInput from '../../../inputs/SelectInput';
import MultiSelectInput from '../../../inputs/MultiSelectInput';
import NoteInput from '../../../inputs/NoteInput';
import BreadCrumb from '../../../BreadCrumb';
import CheckboxInput from '../../../onboarding/inputs/CheckboxInput';
import api from '../../../../api';

type Props = {
  initialData: {
    maxLevel: number,
    course?: Course,
    image?: MediaFile,
    difficultyEnums: { "string": "number" }
    courseCategories: Category[]
    allCategories: Category[]
  }
}

const CreateCourse: React.FunctionComponent<Props> = ({
  initialData: {
    maxLevel,
    difficultyEnums,
    courseCategories,
    allCategories,
    course,
    image,
  },
}: Props): JSX.Element => {
  const [name, setName] = React.useState('');
  const [errorName, setErrorName] = React.useState('');

  const [status, setStatus] = React.useState(0);
  const [language, setLanguage] = React.useState('en');

  const [hackergalLevel, setHackergalLevel] = React.useState(null);
  const [errorHackergalLevel, setErrorHackergalLevel] = React.useState('');

  const [difficultyLevel, setDifficultyLevel] = React.useState(-1)
  const [errorDifficultyLevel, setErrorDifficultyLevel] = React.useState('')

  const [selectedCategories, setSelectedCategories] = React.useState([])

  const [isMiniLesson, setIsMiniLesson] = React.useState(false);

  const [coverImage, setCoverImage] = React.useState(null);

  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (course) {
      setName(course.title);
      setStatus(course.active ? 1 : 0);
      setLanguage(course.language);
      setHackergalLevel(course.required_level);
      setDifficultyLevel(course.difficulty_level);
      setIsMiniLesson(course.mini_lesson || false);
      if (courseCategories) {
        setSelectedCategories(courseCategories.map((c) => c.id));
      }

      if (image) {
        setCoverImage([JSON.parse(image.file_data)]);
      }
    }
  }, []);

  const handleSubmit = () => {
    setLoading(true);

    let error = false;

    if (!(name && name.trim())) {
      setErrorName("This field can't be blank"); //TODO: Add Translations
      setName('');
      error = true;
    }

    if (hackergalLevel === null) {
      setErrorHackergalLevel("This field can't be blank"); //TODO: Add Translations
      error = true;
    }

    if (!error) {
      const body = {
        title: name.trim(),
        language: language,
        required_level: hackergalLevel,
        difficulty_level: difficultyLevel,
        active: !!status,
        mini_lesson: isMiniLesson,
      }

      let formData = new FormData();

      Object.keys(body).forEach((key) => formData.append(`course[${key}]`, body[key]))

      selectedCategories.forEach((id) => {
        formData.append("course[category_ids][]", id);
      });

      if (course) {
        if (coverImage) {
          if (image && image.file_data === JSON.stringify(coverImage[0])) {
            formData.append("course[cover_image]", JSON.stringify(image));
          } else {
            formData.append("course[cover_image]", coverImage[0]);
          }
        }
      } else if (coverImage) {
        formData.append("course[cover_image]", coverImage[0]);
      }

      if (course) {
        api.updateCourse(course.id, formData)
          .then((res) => window.location.href = res.url)
          .catch((error) => console.error("Error: ", error));
      } else {
        api.createCourse(formData)
          .then((res) => window.location.href = res.url)
          .catch((error) => console.error("Error: ", error));
      }
    } else {
      setLoading(false);
    }
  }

  const detectedChanges = () => {
    if (!course) return false

    const newActive = !!status;
    let sameImage = !!image;

    if (!coverImage) {
      sameImage = (image === coverImage);
    } else {
      sameImage = image ? image.file_data === JSON.stringify(coverImage[0]) : false;
    }

    if (
      newActive != course.active
      || name !== course.title
      || hackergalLevel !== course.required_level
      || language !== course.language
      || !sameImage
      || difficultyLevel !== course.difficulty_level
      || selectedCategories !== courseCategories
      || isMiniLesson !== course.mini_lesson
    ) {
      return false;
    }

    return true;
  }

  return (
    <div className="new_course">
      <Header
        title={I18n.t(course ? 'models.course.messaging.edit' : 'models.course.messaging.create')}
      />

      <BreadCrumb
        locations={[
          { title: I18n.t('models.course.attributes.course'), url: '/admin/courses' },
          { title: course ? I18n.t('models.course.messaging.edit') + ` ${course.title}` : I18n.t('models.course.messaging.create'), url: null }
        ]}
      />

      <TextInput
        columnSize={12}
        error={errorName}
        value={name}
        type="courses"
        field="name"
        label={I18n.t('models.course.attributes.name')}
        style={{ marginTop: 60, padding: 0 }}
        className="required"
        onChange={(value) => {
          setName(value);
          setErrorName('');
        }}
      />

      <div className="col-md-12">
        <div className="row">

          <SelectInput
            columnSize={4}
            value={status}
            type="courses"
            field="status"
            label={I18n.t('models.course.attributes.status')}
            style={{ paddingLeft: 0 }}
            onChange={(value) => setStatus(parseInt(value))}
          >
            <option key={0} value={0}>{I18n.t('models.course.status.inactive')}</option>
            <option key={1} value={1}>{I18n.t('models.course.status.active')}</option>
          </SelectInput>

          <SelectInput
            columnSize={4}
            value={(hackergalLevel === null) ? -2 : hackergalLevel}
            error={errorHackergalLevel}
            type="courses"
            field="required_hackergal_level"
            label={I18n.t('models.course.attributes.required_level')}
            style={{ paddingLeft: 0 }}
            className="required"
            onChange={(value) => {
              setHackergalLevel(value)
              setErrorDifficultyLevel('')
            }}
          >
            <>
              {!course && (
                <option key={`level_-2`} value={-2} style={{ display: 'none' }}></option>
              )}
              <option key={`level_-1`} value={-1}>{I18n.t('models.course.required_level.-1')}</option>

              {
                Array.from(Array(maxLevel).keys()).map((x, i) => (
                  <option key={`level_${i}`} value={i}>{i === 0 ? I18n.t('models.course.required_level.0') : i}</option>
                ))
              }
            </>
          </SelectInput>

          <SelectInput
            columnSize={4}
            value={language}
            type="courses"
            field="language"
            label={I18n.t('models.common.language')}
            style={{ paddingRight: 0 }}
            onChange={(value) => setLanguage(value)}
          >
            <option key={0} value={'en'}>{I18n.t('views.common.english')}</option>
            <option key={1} value={'fr'}>{I18n.t('views.common.french')}</option>
          </SelectInput>

          <div className="col-md-4" style={{ paddingRight: 0 }}>
            <div className="form-group">
              <label>{I18n.t('models.course.attributes.mini_lesson') || 'Mini Course'}</label>
              <CheckboxInput
                label={I18n.t('models.course.attributes.is_mini_lesson') || 'Is this a mini course?'}
                name="mini_lesson"
                value="mini_lesson"
                checked={isMiniLesson}
                onChange={(checked) => setIsMiniLesson(checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-12">
        <div className="row">

          <SelectInput
            value={
              difficultyLevel === null ||
                difficultyLevel === undefined ||
                difficultyLevel === '' ||
                difficultyLevel === 'unspecified'
                ? -1
                : difficultyLevel
            }
            type="course"
            field="difficulty_id"
            label="Course Difficulty Level"
            columnSize={6}
            error={errorDifficultyLevel}
            className="required"
            onChange={(value) => {
              setDifficultyLevel(value)
              setErrorDifficultyLevel('')
            }}
          >
            <>
              {Object.entries(difficultyEnums).map(([name, value]) => (
                <option key={value} value={value}>
                  {name.charAt(0).toUpperCase() + name.slice(1)}
                </option>
              ))}
            </>
          </SelectInput>
          <MultiSelectInput
            value={selectedCategories}
            type="course"
            field="category_ids"
            label="Course Categories" //TODO: Add Translations
            columnSize={6}
            onChange={(values) => setSelectedCategories(values)}
          >
            <>
              {allCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} {/* //TODO: Add Translations */}
                </option>
              ))}
            </>
          </MultiSelectInput>
        </div>

      </div>

      <div className="col-md-12 new_course__drag-container drag-drop-container">
        <div className="form-group">
          <label>{I18n.t('models.course.attributes.image')}</label>
          <div className="card">
            <div className="card-body new_course__file-container">
              {
                coverImage ? (
                  <>
                    <span style={{ color: "#333" }} className="text-left">  {coverImage && (coverImage[0].name || coverImage[0].metadata.filename)}</span>
                    <span style={{ color: "#333" }} className="fal fa-trash text-danger" data-index={0} onClick={() => setCoverImage(null)}></span>
                  </>
                ) : (
                  <Dropzone onDrop={file => setCoverImage(file)}>
                    {({ getRootProps, getInputProps }) => (
                      <section className="new_course__section">
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <p className="mb-0">
                            <span style={{ color: "#333" }} className="far fa-file fa-lg me-2 new_course__icon"></span>
                            <span style={{ color: "#333" }} className="text-underline">{I18n.t('views.forms.messaging.drag_drop_files.click_here')}</span>
                          </p>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                )
              }
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 text-center mt-5">
        <button
          type="submit"
          name="commit"
          disabled={loading || detectedChanges()}
          className="btn btn-primary pull-right"
          onClick={handleSubmit}
        >
          {I18n.t('views.forms.submit')}
          {loading && <i className='far fa-spinner-third fa-spin btn-loader ml-2'></i>}
        </button>
      </div>
    </div>
  );
}
export default CreateCourse;
